import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FileText, Loader2, ArrowLeft, Check, RefreshCw, Copy,
  Download, CheckCircle, XCircle, AlertTriangle, Pencil, Eye, Code
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  'Draft': 'bg-slate-100 text-slate-600',
  'Generating...': 'bg-blue-100 text-blue-600',
  'Done': 'bg-green-100 text-green-700',
  'Published': 'bg-purple-100 text-purple-700',
  'Error': 'bg-red-100 text-red-600',
};

const STATUS_I18N = {
  'Draft': 'статус_черновик',
  'Generating...': 'статус_генерация',
  'Done': 'статус_готово',
  'Published': 'статус_опубликовано',
  'Error': 'статус_ошибка',
};

const cleanArticleContent = (md) => {
  if (!md) return '';
  // Remove ```json { ... } ``` metadata blocks
  let text = md.replace(/```json\s*\{[\s\S]*?\}\s*```/g, '');
  // Remove raw JSON metadata blocks (seo_title + word_count pattern)
  text = text.replace(/^\s*\{\s*\n?\s*"seo_title"[\s\S]*?"word_count"\s*:\s*\d+\s*\}\s*/m, '');
  return text.trim();
};


const ArticlesList = ({ monitorId, monitor }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [article, setArticle] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editMeta, setEditMeta] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [improveText, setImproveText] = useState('');
  const [improving, setImproving] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [articleProgress, setArticleProgress] = useState('');
  const [articleStats, setArticleStats] = useState({});
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  useEffect(() => { loadArticles(); }, [monitorId]);
  useEffect(() => {
    const loadStats = async () => {
      try {
        const { data } = await axios.get(API + "/api/pixel/" + monitorId + "/article-stats", { withCredentials: true });
        if (data) setArticleStats(data);
      } catch (e) { /* silent */ }
    };
    loadStats();
  }, [monitorId]);

  // Auto-poll for generating articles (check task status) — with 10 min timeout
  useEffect(() => {
    const generatingArticles = articles.filter(a => a.status === 'Generating...');
    if (generatingArticles.length === 0) return;

    const MAX_POLL_TIME = 600000; // 10 minutes in ms
    const startTime = Date.now();
    const poll = async () => {
      // Stop polling after MAX_POLL_TIME
      if (Date.now() - startTime > MAX_POLL_TIME) {
        setArticleProgress(t('articles.таймаут_генерации'));
        loadArticles();
        return;
      }
      for (const a of generatingArticles) {
        if (a.task_id) {
          try {
            const { data } = await axios.get(`$` + `{API}/api/tasks/$` + `{a.task_id}`, { withCredentials: true });
            setArticleProgress(data.progress || t('articles.статус_генерация'));
            if (data.status === 'completed' || data.status === 'failed') {
              loadArticles();
              if (selectedId === a.id) loadArticle(a.id);
              return;
            }
          } catch { /* ignore */ }
        }
      }
      loadArticles();
    };
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [articles, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadArticles = async () => {
    try {
      const { data } = await axios.get(`${API}/api/promo/${monitorId}/articles`, { withCredentials: true });
      setArticles(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadArticle = async (id) => {
    setLoadingArticle(true);
    setSelectedId(id);
    try {
      const { data } = await axios.get(`${API}/api/promo/${monitorId}/articles/${id}`, { withCredentials: true });
      setArticle(data);
      setEditContent(data.content_md || '');
      setEditMeta(data.meta_description || '');
    } catch (err) { console.error(err); }
    finally { setLoadingArticle(false); }
  };

  const handleSave = async () => {
    if (!article) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/promo/${monitorId}/articles/${article.id}`, {
        content_md: editContent,
        meta_description: editMeta,
      }, { withCredentials: true });
      await loadArticle(article.id);
      setEditMode(false);
    } catch (err) { alert(err.response?.data?.detail || t('articles.ошибка_сохранения')); }
    finally { setSaving(false); }
  };

  const handleApprove = async () => {
    if (!article) return;
    setApproving(true);
    try {
      await axios.post(`${API}/api/promo/${monitorId}/articles/${article.id}/approve`, {}, { withCredentials: true });
      await loadArticle(article.id);
      await loadArticles();
    } catch (err) { alert(err.response?.data?.detail || t('articles.ошибка_утверждения')); }
    finally { setApproving(false); }
  };

  const handleRegenerate = async () => {
    if (!article) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/articles/generate`, {
        topic_id: article.topic_id,
      }, { withCredentials: true });
      setArticle(data);
      setEditContent(data.content_md || '');
      setEditMeta(data.meta_description || '');
      await loadArticles();
    } catch (err) { alert(err.response?.data?.detail || t('articles.ошибка_перегенерации')); }
    finally { setRegenerating(false); }
  };

  const handleImprove = async () => {
    if (!improveText.trim() || !article) return;
    setImproving(true);
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/articles/${article.id}/improve`, {
        article_id: article.id,
        instruction: improveText,
      }, { withCredentials: true });
      setImproveText('');
      // Show the improved text as a suggestion
      setEditContent(data.improved_text || ''); setEditMode(true);
    } catch (err) { alert(err.response?.data?.detail || t('articles.ошибка')); }
    finally { setImproving(false); }
  };

  const copyToClipboard = async (text, format) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(format);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) { console.error(err); }
  };

  const handleDownload = () => {
    if (!article) return;
    const mdWithDisclosure = article.content_md + '\n\n---\n*Generated with AI / AI-assisted*\n';
    const blob = new Blob([mdWithDisclosure], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${article.slug || 'article'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Article detail view
  if (selectedId && article) {
    return (
      <div data-testid="article-editor">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <button onClick={() => { setSelectedId(null); setArticle(null); setEditMode(false); }}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-[#1BB594]"
            data-testid="back-to-articles">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('articles.назад_к_списку')}
          </button>
          <div className="flex gap-2 flex-wrap">
            {article.status !== 'Done' && (
              <>
                <button onClick={() => setEditMode(!editMode)}
                  className="flex items-center gap-1 px-3 py-1.5 border border-[#E2DDD8] text-xs font-bold uppercase text-slate-700 hover:bg-slate-50"
                  data-testid="toggle-edit-btn">
                  {editMode ? <><Eye className="w-3 h-3" /> {t('articles.просмотр')}</> : <><Pencil className="w-3 h-3" /> {t('articles.редактировать')}</>}
                </button>
                <button onClick={handleRegenerate} disabled={regenerating}
                  className="flex items-center gap-1 px-3 py-1.5 border border-[#E2DDD8] text-xs font-bold uppercase text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="regenerate-btn">
                  {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {t('articles.перегенерировать')}
                </button>
                <button onClick={handleApprove} disabled={approving || article.legal_status === 'failed'}
                  className="flex items-center gap-1 px-4 py-1.5 bg-green-600 text-white text-xs font-bold uppercase hover:bg-green-700 disabled:opacity-50"
                  data-testid="approve-btn">
                  {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {t('articles.утвердить')}
                </button>
              </>
            )}
            {article.status === 'Done' && (
              <div className="flex gap-2">
                <button onClick={() => copyToClipboard(article.content_md, 'md')}
                  className={`flex items-center gap-1 px-3 py-1.5 border text-xs font-bold uppercase ${copyFeedback === 'md' ? 'border-green-500 text-green-600' : 'border-[#E2DDD8] text-slate-700 hover:bg-slate-50'}`}
                  data-testid="copy-md-btn">
                  <Copy className="w-3 h-3" /> {copyFeedback === 'md' ? t('articles.скопировано') : 'Markdown'}
                </button>
                <button onClick={() => { const html = article.content_md.replace(/^### (.*$)/gm, '<h3>$1</h3>').replace(/^## (.*$)/gm, '<h2>$1</h2>').replace(/^# (.*$)/gm, '<h1>$1</h1>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>') + '<hr><p style="font-size:12px;color:#999;"><em>Generated with AI / AI-assisted</em></p>'; copyToClipboard(html, 'html'); }}
                  className={`flex items-center gap-1 px-3 py-1.5 border text-xs font-bold uppercase ${copyFeedback === 'html' ? 'border-green-500 text-green-600' : 'border-[#E2DDD8] text-slate-700 hover:bg-slate-50'}`}
                  data-testid="copy-html-btn">
                  <Code className="w-3 h-3" /> {copyFeedback === 'html' ? t('articles.скопировано') : 'HTML'}
                </button>
                <button onClick={handleDownload}
                  className="flex items-center gap-1 px-3 py-1.5 border border-[#E2DDD8] text-xs font-bold uppercase text-slate-700 hover:bg-slate-50"
                  data-testid="download-btn">
                  <Download className="w-3 h-3" /> {t('articles.скачать')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Meta panel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="article-meta-panel">
          <div className="border border-[#E2DDD8] rounded p-3">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Slug</p>
            <p className="text-xs text-slate-800 truncate">{article.slug || '—'}</p>
          </div>
          <div className="border border-[#E2DDD8] rounded p-3">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Focus Keyword</p>
            <p className="text-xs text-slate-800 truncate">{article.focus_keyword || '—'}</p>
          </div>
          <div className="border border-[#E2DDD8] rounded p-3">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{t('articles.слов')}</p>
            <p className="text-xs text-slate-800">{article.word_count || 0}</p>
          </div>
          <div className="border border-[#E2DDD8] rounded p-3">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Meta Description</p>
            <p className="text-xs text-slate-800">{editMode ? (
              <input value={editMeta} onChange={e => setEditMeta(e.target.value)} className="w-full border rounded px-1 py-0.5 text-xs" data-testid="edit-meta-input" />
            ) : (<span className={`${(article.meta_description?.length || 0) > 155 ? 'text-red-600' : ''}`}>{article.meta_description?.length || 0}/155</span>)}</p>
          </div>
        </div>

        {/* Checklist */}
        {article.checklist && (
          <div className="border border-[#E2DDD8] rounded p-3 mb-4" data-testid="article-checklist">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">{t('articles.чеклист')}</p>
            <div className="flex flex-wrap gap-2">
              {article.checklist.map((c, i) => (
                <span key={i} className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded font-medium ${c.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {c.passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  {c.code}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Legal warning */}
        {article.legal_status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 flex items-center gap-2" data-testid="legal-warning">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-xs text-red-800">{t('articles.правовая_проверка_не_пройдена')}: {article.legal_check}</p>
          </div>
        )}

        {/* Editor / Preview */}
        <div className="border border-[#E2DDD8] rounded" data-testid="article-content-area">
          <div className="border-b border-[#E2DDD8] px-4 py-2 bg-slate-50 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-800">{article.title}</h2>
            <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${STATUS_STYLES[article.status] || ''}`}>{t('articles.' + (STATUS_I18N[article.status] || article.status))}</span>
          </div>
          {editMode ? (
            <div>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                className="w-full min-h-[500px] p-4 text-sm font-mono focus:outline-none resize-y"
                data-testid="article-edit-textarea" />
              <div className="border-t border-[#E2DDD8] p-3 flex gap-2">
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase hover:bg-[#159A7F] disabled:opacity-50"
                  data-testid="save-article-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}{t('articles.сохранить')}
                </button>
                <button onClick={() => { setEditMode(false); setEditContent(article.content_md); }}
                  className="px-3 py-2 text-xs text-slate-500 hover:text-slate-800">{t('articles.отмена')}</button>
              </div>
            </div>
          ) : (
              <div className="p-6 max-w-none" data-testid="article-preview" data-ai-generated="true">
                <div className="mb-4 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-2.5 py-1">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8V4H8"/><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
                  Generated with AI
                </div>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({children}) => <h1 className="text-2xl font-bold text-slate-900 mt-8 mb-4 pb-2 border-b border-slate-200">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-bold text-slate-800 mt-6 mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-semibold text-slate-700 mt-5 mb-2">{children}</h3>,
                    h4: ({children}) => <h4 className="text-base font-semibold text-slate-700 mt-4 mb-2">{children}</h4>,
                    p: ({children}) => <p className="text-sm text-slate-600 leading-relaxed mb-3">{children}</p>,
                    ul: ({children}) => <ul className="list-disc pl-5 space-y-1 mb-3 text-sm text-slate-600">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 space-y-1 mb-3 text-sm text-slate-600">{children}</ol>,
                    li: ({children}) => <li className="leading-relaxed">{children}</li>,
                    strong: ({children}) => <strong className="font-bold text-slate-800">{children}</strong>,
                    blockquote: ({children}) => <blockquote className="border-l-4 border-[#1BB594] pl-4 my-4 italic text-slate-500">{children}</blockquote>,
                    pre: ({children}) => <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto my-4 text-xs">{children}</pre>,
                    code: ({className, children, ...props}) => {
                      if (className) return <code className="font-mono" {...props}>{children}</code>;
                      return <code className="bg-slate-100 text-[#1BB594] px-1.5 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>;
                    },
                    table: ({children}) => <div className="overflow-x-auto my-4 rounded-lg border border-slate-200"><table className="min-w-full text-sm">{children}</table></div>,
                    thead: ({children}) => <thead className="bg-slate-100 border-b border-slate-200">{children}</thead>,
                    tbody: ({children}) => <tbody className="divide-y divide-slate-100">{children}</tbody>,
                    th: ({children}) => <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-600">{children}</th>,
                    td: ({children}) => <td className="px-4 py-2.5 text-slate-600">{children}</td>,
                    tr: ({children}) => <tr className="hover:bg-slate-50/50">{children}</tr>,
                    hr: () => <hr className="my-6 border-slate-200" />,
                    a: ({href, children}) => <a href={href} className="text-[#1BB594] underline hover:text-[#159A7F]" target="_blank" rel="noopener noreferrer">{children}</a>,
                  }}
                >{cleanArticleContent(article.content_md)}</ReactMarkdown>
              </div>
          )}
        </div>

        {/* Improve section */}
        {article.status !== 'Done' && (
          <div className="mt-4 border border-[#E2DDD8] rounded p-4" data-testid="improve-section">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">{t('articles.улучшить_раздел')}</p>
            <div className="flex gap-2">
              <input value={improveText} onChange={e => setImproveText(e.target.value)}
                placeholder={t('articles.инструкция_placeholder')}
                className="flex-1 border border-[#E2DDD8] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#1BB594]"
                data-testid="improve-input" />
              <button onClick={handleImprove} disabled={improving || !improveText.trim()}
                className="px-4 py-2 border border-[#1BB594]/30 text-[#1BB594] text-xs font-bold uppercase hover:bg-[#1BB594] hover:text-white disabled:opacity-40 transition-colors"
                data-testid="improve-btn">
                {improving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('articles.улучшить')}
              </button>
            </div>
          </div>
        )}

        {/* Versions */}
        {article.versions?.length > 0 && (
          <div className="mt-4 border border-[#E2DDD8] rounded p-4" data-testid="versions-section">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">{t('articles.история_версий', { count: article.versions.length })}</p>
            <div className="space-y-1">
              {article.versions.map((v, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-slate-600 py-1 border-b border-[#E2DDD8]/50">
                  <span>{t('articles.версия', { num: i + 1 })}: {v.title}</span>
                  <span className="text-slate-400">{v.saved_at ? new Date(v.saved_at).toLocaleDateString(dateLocale) : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Articles list view
  return (
    <div data-testid="articles-list">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-800">{t('articles.статьи', { count: articles.length })}</h3>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 text-[#1BB594] animate-spin mx-auto" /></div>
      ) : articles.length === 0 ? (
        <div className="py-12 text-center text-slate-400 border border-[#E2DDD8] rounded">
          <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">{t('articles.нет_статей')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <button key={a.id} onClick={() => loadArticle(a.id)}
              className="w-full text-left border border-[#E2DDD8] rounded p-4 hover:border-[#1BB594]/30 transition-colors"
              data-testid={`article-item-${a.id}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800 truncate">{a.title || t('articles.без_заголовка')}</p>
                  <div className="flex gap-2 items-center mt-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${STATUS_STYLES[a.status] || 'bg-slate-100 text-slate-600'}`}>{t('articles.' + (STATUS_I18N[a.status] || a.status))}</span>
                    {a.status === 'Generating...' && (
                      <span className="text-[10px] text-blue-500 flex items-center gap-1" data-testid="article-progress">
                        <Loader2 className="w-3 h-3 animate-spin" /> {articleProgress || t('articles.подготовка')}
                      </span>
                    )}
                    {a.status !== 'Generating...' && <span className="text-[10px] text-slate-400">{a.word_count || 0} {t('articles.слов_кратко')}</span>}
                    {a.checklist_passed !== undefined && a.status !== 'Generating...' && (
                      <span className={`text-[10px] ${a.checklist_passed ? 'text-green-600' : 'text-amber-600'}`}>
                        {a.checklist_passed ? t('articles.чеклист_пройден') : t('articles.чеклист_не_пройден')}
                      </span>
                    )}
                    {articleStats[a.id] > 0 && (
                      <span className="text-[10px] text-blue-600 flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {articleStats[a.id]} {t('articles.просм')}
                      </span>
                    )}
                  </div>
                </div>
                <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticlesList;
